import React from "react";
import { useTranslation } from "react-i18next";
import useLangRoute from "@/hooks/useLangRoute";
import ContactForm from "@/components/ContactForm";
import { ArrowRight, Building2, BadgeCheck, LineChart, Landmark, Shield, Sparkles, Compass, MapPin, Clock, Mail } from "lucide-react";

// Argentine high-rise / new residential building photos (Unsplash direct)
const IMG_HERO = "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=80"; // high-rise dusk
const IMG_CITY = "https://images.unsplash.com/photo-1589909202802-8f4aadce1849?auto=format&fit=crop&w=1400&q=80"; // Buenos Aires skyline
const IMG_P1 = "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=900&q=80"; // residential towers
const IMG_P2 = "https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=900&q=80"; // new residential complex
const IMG_P3 = "https://images.unsplash.com/photo-1580537659466-0a9bfa916a54?auto=format&fit=crop&w=900&q=80"; // heritage building refurb
const IMG_P4 = "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=900&q=80"; // urban skyline

export default function HomePage() {
  const { t } = useTranslation();
  const { path } = useLangRoute();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[#F7F3EA]" />
          <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#E9B949]/20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#0F1B2D]/10 blur-3xl" />
        </div>
        <div className="mx-auto max-w-6xl px-5 pt-14 pb-16 md:pt-20 md:pb-24 grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#0F1B2D]/15 bg-white/60 px-3 py-1 text-[11.5px] font-medium uppercase tracking-wider text-[#0F1B2D]">
              <Sparkles className="h-3.5 w-3.5 text-[#B3862B]" />
              {t("hero.eyebrow")}
            </span>
            <h1 className="mt-5 text-[36px] leading-[1.05] md:text-[56px] md:leading-[1.03] font-semibold tracking-tight text-[#0F1B2D]">
              {t("hero.title")}
            </h1>
            <p className="mt-5 max-w-xl text-[15.5px] md:text-[17px] leading-relaxed text-[#4b5563]">
              {t("hero.subtitle")}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                onClick={scrollTo("contact")}
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-[#0F1B2D] px-5 py-3 text-[14px] font-semibold text-[#F7F3EA] hover:bg-[#1c2c46] transition-colors"
              >
                {t("hero.ctaPrimary")} <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#how"
                onClick={scrollTo("how")}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-[#0F1B2D]/20 bg-white/60 px-5 py-3 text-[14px] font-semibold text-[#0F1B2D] hover:bg-white transition-colors"
              >
                {t("hero.ctaSecondary")}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <Stat value={t("hero.stat1Value")} label={t("hero.stat1")} />
              <Stat value={t("hero.stat2Value")} label={t("hero.stat2")} />
              <Stat value={t("hero.stat3Value")} label={t("hero.stat3")} />
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-[28px] overflow-hidden shadow-2xl ring-1 ring-black/5">
              <img
                src={IMG_HERO}
                alt="High-rise residential towers in Argentina"
                className="w-full h-[420px] md:h-[520px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/60 to-transparent">
                <div className="flex items-center gap-2 text-white text-[12px]">
                  <Building2 className="h-4 w-4" />
                  {t("hero.eyebrow")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white border-y border-[#0F1B2D]/8">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-20 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-[#B3862B]">{t("intro.kicker")}</p>
            <h2 className="mt-3 text-[28px] md:text-[36px] font-semibold tracking-tight text-[#0F1B2D] leading-tight">
              {t("intro.title")}
            </h2>
            <div className="mt-6 rounded-2xl overflow-hidden ring-1 ring-black/5">
              <img src={IMG_CITY} alt="Buenos Aires skyline" className="w-full h-64 object-cover" loading="lazy" />
            </div>
          </div>
          <div className="md:col-span-8 space-y-4 text-[15px] leading-relaxed text-[#374151]">
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
            <p>{t("intro.p3")}</p>
            <p>{t("intro.p4")}</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="scroll-mt-20 bg-[#F7F3EA]">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-[#B3862B]">{t("how.kicker")}</p>
            <h2 className="mt-3 text-[28px] md:text-[40px] font-semibold tracking-tight text-[#0F1B2D] leading-tight">
              {t("how.title")}
            </h2>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-4">
            <StepCard n="01" icon={<Compass className="h-5 w-5" />} title={t("how.steps.s1Title")} body={t("how.steps.s1Body")} />
            <StepCard n="02" icon={<Landmark className="h-5 w-5" />} title={t("how.steps.s2Title")} body={t("how.steps.s2Body")} />
            <StepCard n="03" icon={<LineChart className="h-5 w-5" />} title={t("how.steps.s3Title")} body={t("how.steps.s3Body")} />
            <StepCard n="04" icon={<BadgeCheck className="h-5 w-5" />} title={t("how.steps.s4Title")} body={t("how.steps.s4Body")} />
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 text-[15px] leading-relaxed text-[#374151]">
            <p>{t("how.p1")}</p>
            <p>{t("how.p2")}</p>
            <p>{t("how.p3")}</p>
            <p>{t("how.p4")}</p>
            <p>{t("how.p5")}</p>
            <p>{t("how.p6")}</p>
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="scroll-mt-20 bg-white border-y border-[#0F1B2D]/8">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-[12px] font-semibold uppercase tracking-wider text-[#B3862B]">{t("projects.kicker")}</p>
              <h2 className="mt-3 text-[28px] md:text-[40px] font-semibold tracking-tight text-[#0F1B2D] leading-tight">{t("projects.title")}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[#4b5563]">{t("projects.subtitle")}</p>
            </div>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <ProjectCard img={IMG_P1} city={t("projects.p1City")} title={t("projects.p1Title")} body={t("projects.p1Body")} />
            <ProjectCard img={IMG_P2} city={t("projects.p2City")} title={t("projects.p2Title")} body={t("projects.p2Body")} />
            <ProjectCard img={IMG_P3} city={t("projects.p3City")} title={t("projects.p3Title")} body={t("projects.p3Body")} />
            <ProjectCard img={IMG_P4} city={t("projects.p4City")} title={t("projects.p4Title")} body={t("projects.p4Body")} />
          </div>
        </div>
      </section>

      {/* TRUST / ABOUT */}
      <section id="about" className="scroll-mt-20 bg-[#0F1B2D] text-[#EDE7DA]">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-[#E9B949]">{t("trust.kicker")}</p>
            <h2 className="mt-3 text-[28px] md:text-[40px] font-semibold tracking-tight leading-tight text-white">
              {t("trust.title")}
            </h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-4">
            <TrustCard icon={<LineChart className="h-5 w-5" />} title={t("trust.t1Title")} body={t("trust.t1Body")} />
            <TrustCard icon={<BadgeCheck className="h-5 w-5" />} title={t("trust.t2Title")} body={t("trust.t2Body")} />
            <TrustCard icon={<Landmark className="h-5 w-5" />} title={t("trust.t3Title")} body={t("trust.t3Body")} />
            <TrustCard icon={<Shield className="h-5 w-5" />} title={t("trust.t4Title")} body={t("trust.t4Body")} />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="scroll-mt-20 bg-[#F7F3EA]">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24 grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-[#B3862B]">{t("contact.kicker")}</p>
            <h2 className="mt-3 text-[28px] md:text-[40px] font-semibold tracking-tight text-[#0F1B2D] leading-tight">
              {t("contact.title")}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-[#4b5563]">{t("contact.subtitle")}</p>

            <div className="mt-8 space-y-4">
              <ContactRow icon={<MapPin className="h-4 w-4" />} label={t("contact.addressLabel")} value={t("contact.addressValue")} />
              <ContactRow icon={<Clock className="h-4 w-4" />} label={t("contact.hoursLabel")} value={t("contact.hoursValue")} />
              <ContactRow
                icon={<Mail className="h-4 w-4" />}
                label={t("contact.emailUs")}
                value={
                  <a href="mailto:hola@mirexiluna.com" className="underline decoration-[#B3862B] underline-offset-4 hover:text-[#0F1B2D]" data-testid="contact-email-link">
                    hola@mirexiluna.com
                  </a>
                }
              />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-2xl border border-[#0F1B2D]/10 bg-white p-6 md:p-8 shadow-sm">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="text-[26px] md:text-[30px] font-semibold text-[#0F1B2D] leading-none">{value}</p>
      <p className="mt-1.5 text-[12px] text-[#4b5563] leading-snug">{label}</p>
    </div>
  );
}

function StepCard({ n, icon, title, body }) {
  return (
    <div className="group rounded-2xl border border-[#0F1B2D]/10 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono text-[#B3862B]">{n}</span>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0F1B2D] text-[#F7F3EA]">{icon}</span>
      </div>
      <h3 className="mt-5 text-[16px] font-semibold text-[#0F1B2D]">{title}</h3>
      <p className="mt-2 text-[13.5px] leading-relaxed text-[#4b5563]">{body}</p>
    </div>
  );
}

function ProjectCard({ img, city, title, body }) {
  return (
    <article className="group rounded-2xl overflow-hidden border border-[#0F1B2D]/10 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={img}
          alt={title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-6">
        <p className="text-[11.5px] font-medium uppercase tracking-wider text-[#B3862B]">{city}</p>
        <h3 className="mt-2 text-[18px] font-semibold text-[#0F1B2D]">{title}</h3>
        <p className="mt-2 text-[14px] leading-relaxed text-[#4b5563]">{body}</p>
      </div>
    </article>
  );
}

function TrustCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E9B949] text-[#0F1B2D]">{icon}</span>
      <h3 className="mt-4 text-[16px] font-semibold text-white">{title}</h3>
      <p className="mt-2 text-[13.5px] leading-relaxed text-[#EDE7DA]/80">{body}</p>
    </div>
  );
}

function ContactRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#0F1B2D]/8 text-[#0F1B2D]">{icon}</span>
      <div>
        <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#B3862B]">{label}</p>
        <p className="mt-1 text-[14px] leading-relaxed text-[#0F1B2D]">{value}</p>
      </div>
    </div>
  );
}
